import React, { useEffect } from 'react';
import { FieldState } from 'formstate';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { YlDropdown } from '../../../../infrastructure/forms/YlDropdown';
import { PaymentInstallment } from './PaymentInstallment';
import { useRootStore } from '../../../root/RootStoreHooks';

interface Props {
  keyAsLabel?: boolean;
}

export const PaymentInstallmentDropdown = observer<Props>(({ keyAsLabel }) => {
  const { t } = useTranslation();
  const {
    moduleStores,
    reference: { currency }
  } = useRootStore();
  const { billingEntry } = moduleStores!;

  const { installmentEntry, selectedPaymentType } = billingEntry!;
  const { availableInstallments, isEnabled, selectedInstallment } = installmentEntry;

  useEffect(() => {
    if (!availableInstallments && isEnabled) {
      installmentEntry.getAvailableInstallments();
    }
  }, [installmentEntry, availableInstallments, isEnabled, selectedPaymentType]);

  useEffect(() => {
    if (!selectedInstallment && availableInstallments) {
      const fullPaymentInstallment = availableInstallments.find(x => x.installmentNumber === 1);
      if (fullPaymentInstallment) {
        installmentEntry.changeSelectedInstallment(fullPaymentInstallment);
      }
    }
  }, [installmentEntry, availableInstallments, selectedInstallment]);

  return (
    <YlDropdown
      size={12}
      valueFn={(installment: PaymentInstallment) => installment}
      labelFn={(installment: PaymentInstallment) => {
        if (keyAsLabel) {
          return installment.installmentKey;
        }

        if (installment.installmentNumber === 1) {
          return t('InstallmentFullPayment');
        }
        return (
          t('InstallmentIndependent') +
          ` ${installment.installmentNumber}x` +
          ` - ${currency.symbol}${currency.formatDisplayAmount(installment.installmentAmount)} ${currency.code}` +
          ` (${currency.symbol}${currency.formatDisplayAmount(installment.totalAmount)} ${currency.code} ${t(
            'InstallmentInTotal'
          )})`
        );
      }}
      placeholder=""
      loading={installmentEntry.isInstallmentsLoading}
      options={availableInstallments ?? []}
      field={new FieldState(selectedInstallment)}
      required
      id="installment"
      label={t('PaymentInstallmentLabel')}
      onChange={(installment: any) => installmentEntry.changeSelectedInstallment(installment)}
    />
  );
});
